"use client"

import { useEffect, useRef, useState } from 'react'

interface CryptoPriceUpdate {
  symbol: string
  price: number
  priceChange: number
  priceChangePercent: number
  high?: number
  low?: number
  volume?: number
  quoteVolume?: number
}

interface UseCryptoWebSocketOptions {
  symbols: string[] // e.g., ['BTCUSDT', 'ETHUSDT']
  enabled?: boolean
}

export function useCryptoWebSocket({ symbols, enabled = true }: UseCryptoWebSocketOptions) {
  const [prices, setPrices] = useState<Map<string, CryptoPriceUpdate>>(new Map())
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5

  const pricesRef = useRef<Map<string, CryptoPriceUpdate>>(new Map())
  const lastUpdateRef = useRef<number>(0)
  const throttleInterval = 1000 // Only update state once per second max
  const currentSymbolsRef = useRef<string[]>(symbols)

  // Keep currentSymbolsRef in sync
  useEffect(() => {
    currentSymbolsRef.current = symbols
  }, [symbols])

  useEffect(() => {
    if (!enabled || symbols.length === 0) return

    // Clear stale data from previous symbol subscriptions
    // This prevents flashing old symbol data (e.g., BTC data when viewing ETH)
    pricesRef.current = new Map()
    setPrices(new Map())

    const connect = () => {
      // Don't attempt to reconnect if we've exceeded max attempts
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        return
      }

      try {
        // Close existing connection if any
        if (wsRef.current) {
          wsRef.current.close()
        }

        // Create streams string for multiple symbols
        // Format: btcusdt@ticker/ethusdt@ticker/solusdt@ticker
        const streams = symbols
          .map(symbol => `${symbol.toLowerCase()}@ticker`)
          .join('/')

        // Use wss:// protocol without port for better compatibility
        const wsUrl = `wss://stream.binance.com/stream?streams=${streams}`
        
        console.log('Connecting to Binance WebSocket:', wsUrl)
        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        ws.onopen = () => {
          console.log('Binance WebSocket connected successfully')
          setIsConnected(true)
          reconnectAttemptsRef.current = 0 // Reset counter on successful connection
        }

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            
            if (message.data) {
              const data = message.data
              
              // Guard: only process data for currently subscribed symbols
              // This prevents stale data from a closing WebSocket from leaking through
              if (!currentSymbolsRef.current.includes(data.s)) {
                return
              }
              
              // Update ref immediately for latest data
              pricesRef.current.set(data.s, {
                symbol: data.s,
                price: parseFloat(data.c),
                priceChange: parseFloat(data.p),
                priceChangePercent: parseFloat(data.P),
                high: parseFloat(data.h),
                low: parseFloat(data.l),
                volume: parseFloat(data.v),
                quoteVolume: parseFloat(data.q),
              })

              // Throttle state updates to prevent render flooding
              const now = Date.now()
              if (now - lastUpdateRef.current >= throttleInterval) {
                setPrices(new Map(pricesRef.current))
                lastUpdateRef.current = now
              }
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

        ws.onerror = (error) => {
          // Silently handle WebSocket errors - they're not critical for the app
          setIsConnected(false)
        }

        ws.onclose = (event) => {
          setIsConnected(false)
          
          // Only attempt to reconnect if not a normal closure and haven't exceeded max attempts
          if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
            // Attempt to reconnect with exponential backoff
            reconnectAttemptsRef.current++
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect()
            }, delay)
          }
        }
      } catch (error) {
        // Silently handle connection errors - WebSocket is optional for enhanced UX
        setIsConnected(false)
      }
    }

    connect()

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [symbols.join(','), enabled]) // Use symbols.join(',') to avoid recreating on array reference change

  return {
    prices,
    isConnected,
  }
}
